import type {
  PriceAnomalyStatus,
  PriceVarianceFilters,
  VarianceGroupedSort,
  VarianceSort,
} from "../../api/reports";

import type {
  DeliveryKind,
  GroupMode,
  PriceVariancePreset,
  PriceVarianceView,
} from "./types";
import { ANALYSIS_PERIODS, analysisPeriodRange, type AnalysisPeriod } from "./analysisPeriod";

export const DEFAULT_MIN_DELTA_PCT = 15;
export const SPIKE_MIN_DELTA_PCT = 25;
export const SPIKE_MIN_REFERENCE_COUNT = 2;

const FLAT_SORT_VALUES: readonly VarianceSort[] = [
  "overpay_desc",
  "overpay_asc",
  "delta_pct_desc",
  "delta_pct_asc",
  "doc_date_desc",
  "doc_date_asc",
  "net_price_desc",
  "net_price_asc",
];
const GROUPED_SORT_VALUES: readonly VarianceGroupedSort[] = [
  "overpay_desc",
  "spend_desc",
  "lines_desc",
];
const GROUP_MODE_VALUES: readonly GroupMode[] = [
  "none",
  "manufacturer",
  "project",
  "delivery_address",
];
const VIEW_VALUES: readonly PriceVarianceView[] = ["overview", "positions", "purchases"];

export type PriceVarianceUrlState = {
  preset: PriceVariancePreset | null;
  view: PriceVarianceView;
  /** Named period of the Analyse; resolves to from/to on every load. */
  period: AnalysisPeriod | null;
  filters: PriceVarianceFilters;
  sort: VarianceSort;
  groupedSort: VarianceGroupedSort;
  groupMode: GroupMode;
};

export function parsePriceVarianceUrlState(
  searchParams: URLSearchParams,
  defaults?: {
    minDeltaPct: number;
    minOverpayAbs: number | null;
  },
): PriceVarianceUrlState {
  const preset =
    searchParams.get("preset") === "historical_spikes"
      ? "historical_spikes"
      : null;
  const filters: PriceVarianceFilters = {
    reference_quality: searchParams.get("reference_quality") === "insufficient" ? "insufficient" : "sufficient",
    min_delta_pct:
      preset === "historical_spikes"
        ? SPIKE_MIN_DELTA_PCT
        : (defaults?.minDeltaPct ?? DEFAULT_MIN_DELTA_PCT),
    ...(preset === null && defaults?.minOverpayAbs != null
      ? { min_overpay_abs: defaults.minOverpayAbs }
      : {}),
    ...(preset === null ? { price_anomaly_status: "open" as const } : {}),
  };

  const period = parseChoice(searchParams.get("period"), ANALYSIS_PERIODS);
  if (period && period !== "custom") {
    // A named period always means "as of today", never the dates of the day
    // the link was made.
    const range = analysisPeriodRange(period);
    if (range.from) filters.from = range.from;
    if (range.to) filters.to = range.to;
  } else {
    setStringFilter(filters, "from", searchParams.get("from"));
    setStringFilter(filters, "to", searchParams.get("to"));
  }
  if (searchParams.get("positive_only") === "true") filters.positive_only = true;
  setStringFilter(filters, "online_user", searchParams.get("online_user"));
  setArrayFilter(filters, "project", searchParams.getAll("project"));
  setArrayFilter(filters, "article", searchParams.getAll("article"));
  setArrayFilter(
    filters,
    "manufacturer_id",
    searchParams.getAll("manufacturer_id"),
  );
  setArrayFilter(filters, "supplier", searchParams.getAll("supplier"));
  setArrayFilter(
    filters,
    "delivery_address_key",
    searchParams.getAll("delivery_address_key"),
  );

  const reviewStatus = searchParams.get("price_anomaly_status");
  if (reviewStatus === "all") {
    delete filters.price_anomaly_status;
  } else {
    const parsedStatus = parsePriceAnomalyStatus(reviewStatus);
    if (parsedStatus) filters.price_anomaly_status = parsedStatus;
  }

  const rawMinDelta = searchParams.get("min_delta_pct");
  if (rawMinDelta === "all") {
    delete filters.min_delta_pct;
  } else {
    const minDelta = parseNonNegativeNumber(rawMinDelta);
    if (minDelta !== null) filters.min_delta_pct = minDelta;
  }
  const rawMinOverpay = searchParams.get("min_overpay_abs");
  if (rawMinOverpay === "all") {
    delete filters.min_overpay_abs;
  } else {
    const minOverpay = parseNonNegativeNumber(rawMinOverpay);
    if (minOverpay !== null) filters.min_overpay_abs = minOverpay;
  }
  const rawMinReferenceCount = searchParams.get("min_reference_count");
  const minReferenceCount = parsePositiveInteger(rawMinReferenceCount);
  if (minReferenceCount !== null) {
    filters.min_reference_count = minReferenceCount;
  } else if (
    preset === "historical_spikes" &&
    rawMinReferenceCount !== "all"
  ) {
    filters.min_reference_count = SPIKE_MIN_REFERENCE_COUNT;
  }

  const warehouse = searchParams.get("is_warehouse");
  if (warehouse === "true") filters.is_warehouse = true;
  if (warehouse === "false") filters.is_warehouse = false;

  const defaultSort: VarianceSort =
    preset === "historical_spikes" ? "delta_pct_desc" : "overpay_desc";
  return {
    preset,
    period,
    view:
      parseChoice(searchParams.get("view"), VIEW_VALUES) ?? "overview",
    filters,
    sort: parseChoice(searchParams.get("sort"), FLAT_SORT_VALUES) ?? defaultSort,
    groupedSort:
      parseChoice(searchParams.get("grouped_sort"), GROUPED_SORT_VALUES) ??
      "overpay_desc",
    groupMode:
      parseChoice(searchParams.get("group_by"), GROUP_MODE_VALUES) ?? "none",
  };
}

export function serializePriceVarianceUrlState(
  state: PriceVarianceUrlState,
): URLSearchParams {
  const params = new URLSearchParams();
  if (state.preset) params.set("preset", state.preset);
  if (state.view !== "overview") params.set("view", state.view);
  if (state.period) params.set("period", state.period);
  appendFilterParams(params, state.filters);
  params.set("sort", state.sort);
  params.set("grouped_sort", state.groupedSort);
  params.set("group_by", state.groupMode);
  return params;
}

export function deliveryKindFor(
  isWarehouse: boolean | undefined,
): DeliveryKind {
  if (isWarehouse === true) return "lager";
  if (isWarehouse === false) return "baustellen";
  return "beides";
}

function appendFilterParams(
  params: URLSearchParams,
  filters: PriceVarianceFilters,
) {
  if (filters.reference_quality) params.set("reference_quality", filters.reference_quality);
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  if (filters.online_user) params.set("online_user", filters.online_user);
  appendValues(params, "project", filters.project);
  appendValues(params, "article", filters.article);
  appendValues(params, "manufacturer_id", filters.manufacturer_id);
  appendValues(params, "supplier", filters.supplier);
  appendValues(params, "delivery_address_key", filters.delivery_address_key);
  params.set(
    "price_anomaly_status",
    filters.price_anomaly_status ?? "all",
  );
  params.set(
    "min_delta_pct",
    filters.min_delta_pct != null ? String(filters.min_delta_pct) : "all",
  );
  params.set(
    "min_overpay_abs",
    filters.min_overpay_abs != null ? String(filters.min_overpay_abs) : "all",
  );
  params.set(
    "min_reference_count",
    filters.min_reference_count != null
      ? String(filters.min_reference_count)
      : "all",
  );
  if (filters.is_warehouse != null) {
    params.set("is_warehouse", filters.is_warehouse ? "true" : "false");
  }
  if (filters.positive_only) params.set("positive_only", "true");
}

function appendValues(
  params: URLSearchParams,
  key: string,
  values: readonly string[] | undefined,
) {
  for (const value of values ?? []) params.append(key, value);
}

function setStringFilter<K extends "from" | "to" | "online_user">(
  filters: PriceVarianceFilters,
  key: K,
  value: string | null,
) {
  if (value) filters[key] = value;
}

function setArrayFilter<
  K extends
    | "project"
    | "article"
    | "manufacturer_id"
    | "supplier"
    | "delivery_address_key",
>(filters: PriceVarianceFilters, key: K, values: string[]) {
  if (values.length) filters[key] = values;
}

function parseChoice<T extends string>(
  value: string | null,
  choices: readonly T[],
): T | null {
  return value !== null && choices.includes(value as T) ? (value as T) : null;
}

function parseNonNegativeNumber(value: string | null): number | null {
  if (value == null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function parsePositiveInteger(value: string | null): number | null {
  if (value == null || value === "") return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 1 ? parsed : null;
}

function parsePriceAnomalyStatus(value: string | null): PriceAnomalyStatus | null {
  if (value === "open" || value === "in_review" || value === "resolved") {
    return value;
  }
  return null;
}
